import { useCallback, useEffect, useState } from 'react';
import { adminApi, type AdminUserProfile, type AdminUserSummary, type ImportPurchaseHistoryPayload, type ImportSummary, type StoreBranch } from '../lib/domain';
import { ApiError } from '../lib/api';
import { parseHistoryCsv } from '../lib/importCsv';

const CSV_COLUMNS: Array<[string, string, string]> = [
  ['Datum', 'Pflicht', 'Kaufdatum, TT.MM.JJJJ oder JJJJ-MM-TT (auch mit Uhrzeit). Wird als Kaufdatum und für Community-Preise verwendet.'],
  ['Geschäft', 'Pflicht', 'Name der Handelskette, z. B. Rewe. Fehlende Ketten werden angelegt.'],
  ['Filiale', 'optional', 'Name der Filiale. Ohne Angabe wird Ort bzw. Kettenname verwendet.'],
  ['Adresse', 'optional', 'Straße und Hausnummer; unterscheidet Filialen gleichen Namens.'],
  ['Ort', 'optional', 'Ort der Filiale.'],
  ['PLZ', 'optional', 'Postleitzahl der Filiale.'],
  ['Produkt', 'Pflicht', 'Produktname. Bestehende Produkte (EAN, sonst Name + Marke) werden wiederverwendet, sonst angelegt.'],
  ['Marke', 'optional', 'Marke des Produkts.'],
  ['EAN', 'optional', '8-14 Ziffern, wird als Barcode hinterlegt.'],
  ['Menge', 'Pflicht', 'Gekaufte Menge, größer 0 (Dezimalkomma erlaubt).'],
  ['Einheit', 'optional', 'Einheitencode des Bereichs, z. B. STUECK (Standard), PACKUNG, FLASCHE, DOSE, BEUTEL, KG, G, MG, L, ML oder eine selbst angelegte Einheit (Gruppenseite).'],
  ['Einzelpreis', 'Preis*', 'Preis je Einheit in Euro.'],
  ['Gesamtpreis', 'Preis*', 'Positionspreis in Euro. *Einzel- oder Gesamtpreis ist Pflicht; der jeweils andere wird berechnet.'],
];

const CSV_EXAMPLE = `Datum;Geschäft;Filiale;Ort;Produkt;Marke;EAN;Menge;Einheit;Einzelpreis;Gesamtpreis
01.03.2025;Rewe;Mitte;Berlin;Vollmilch 1 l;Bio;4000000000017;2;STUECK;1,19;
01.03.2025;Rewe;Mitte;Berlin;Butter;;;1;PACKUNG;;2,49
08.03.2025;Aldi;Nord;Berlin;Äpfel;;;1,5;KG;2,29;`;

function PurchaseHistoryImport() {
  const [targetType, setTargetType] = useState<'user' | 'group'>('user');
  const [userEmail, setUserEmail] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState<Array<{ id: string; name: string; communityOptIn: boolean }>>([]);
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.groups().then((r) => setGroups(r.groups));
  }, []);

  const hasTarget = targetType === 'user' ? !!userEmail : !!groupId;

  async function run(dryRun: boolean) {
    setError(null);
    setResult(null);
    let purchases: ImportPurchaseHistoryPayload['purchases'];
    try {
      purchases = parseHistoryCsv(csv);
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    setBusy(true);
    try {
      const { summary }: { summary: ImportSummary } = await adminApi.importPurchaseHistory({
        ...(targetType === 'user' ? { userEmail } : { groupId }),
        dryRun,
        purchases,
      });
      setResult(
        `${dryRun ? 'Test (nichts gespeichert)' : 'Importiert'}: ${summary.purchasesCreated} Einkäufe, ${summary.purchaseItemsCreated} Positionen, ` +
          `${summary.productsCreated} neue Produkte, ${summary.chainsCreated} neue Ketten, ${summary.branchesCreated} neue Filialen. ` +
          (summary.communityOptIn
            ? dryRun
              ? 'Community-Freigabe aktiv: Preise werden beim Import mit dem Kaufdatum veröffentlicht.'
              : `${summary.communityPricesCreated} Community-Preise erzeugt.`
            : 'Keine Community-Freigabe des Ziels: keine Community-Preise.'),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onFile(file: File | undefined) {
    if (file) setCsv(await file.text());
  }

  return (
    <div className="card stack">
      <h2>Einkaufshistorie importieren</h2>

      <details open>
        <summary>Aufbau der CSV-Datei</summary>
        <div className="stack">
          <p className="muted">
            UTF-8, erste Zeile ist die Kopfzeile mit den Spaltennamen (Reihenfolge beliebig, Groß-/Kleinschreibung egal). Trennzeichen ; oder ,
            (Werte mit Trennzeichen in Anführungszeichen). Zeilen mit gleichem Datum, Geschäft, Filiale und Adresse bilden einen Einkauf.
            Fehlende Geschäfte, Filialen und Produkte werden automatisch angelegt. Bei aktiver Community-Freigabe des Ziels fließen die Preise
            mit dem angegebenen Kaufdatum in die Community-Daten ein.
          </p>
          <table>
            <thead>
              <tr><th>Spalte</th><th>Angabe</th><th>Beschreibung</th></tr>
            </thead>
            <tbody>
              {CSV_COLUMNS.map(([name, req, desc]) => (
                <tr key={name}><td><code>{name}</code></td><td>{req}</td><td>{desc}</td></tr>
              ))}
            </tbody>
          </table>
          <p>Beispiel:</p>
          <pre>{CSV_EXAMPLE}</pre>
          <button type="button" onClick={() => setCsv(CSV_EXAMPLE)}>Beispiel einfügen</button>
        </div>
      </details>

      <fieldset className="stack">
        <legend>Importziel</legend>
        <div className="row">
          <label><input type="radio" checked={targetType === 'user'} onChange={() => setTargetType('user')} /> Benutzer</label>
          <label><input type="radio" checked={targetType === 'group'} onChange={() => setTargetType('group')} /> Gruppe</label>
        </div>
        {targetType === 'user' ? (
          <label>
            E-Mail des Zielbenutzers
            <input type="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
          </label>
        ) : (
          <label>
            Zielgruppe
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Bitte wählen</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}{g.communityOptIn ? ' (Community aktiv)' : ''}</option>
              ))}
            </select>
          </label>
        )}
      </fieldset>

      <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => onFile(e.target.files?.[0])} />
      <textarea rows={8} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="CSV hier einfügen oder Datei wählen" />
      <div className="row">
        <button disabled={busy || !hasTarget || !csv} onClick={() => run(true)}>Testlauf</button>
        <button disabled={busy || !hasTarget || !csv} onClick={() => run(false)}>Importieren</button>
      </div>
      {result && <p role="status">{result}</p>}
      {error && <p role="alert" className="error">{error}</p>}
    </div>
  );
}

function UserProfileEditor({ userId, onChanged }: { userId: string; onChanged: () => void }) {
  const [profile, setProfile] = useState<AdminUserProfile | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function apply(p: AdminUserProfile) {
    setProfile(p);
    setUsername(p.username ?? '');
    setEmail(p.email);
  }

  useEffect(() => {
    adminApi.user(userId).then((r) => apply(r.user)).catch((err) => setError(err instanceof ApiError ? err.message : 'Profil konnte nicht geladen werden.'));
  }, [userId]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!profile) return;
    setMessage(null);
    setError(null);
    const data: { username?: string; email?: string } = {};
    if (username !== (profile.username ?? '')) data.username = username.trim();
    if (email !== profile.email) data.email = email.trim();
    if (Object.keys(data).length === 0) return;
    try {
      const res = await adminApi.updateUser(userId, data);
      apply(res.user);
      setMessage(data.email ? 'Gespeichert. Die neue E-Mail-Adresse muss vom Benutzer bestätigt werden.' : 'Gespeichert.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function sendReset() {
    setMessage(null);
    setError(null);
    try {
      await adminApi.sendPasswordReset(userId);
      setMessage('Link zum Zurücksetzen des Passworts wurde per E-Mail versendet.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Versand fehlgeschlagen.');
    }
  }

  if (!profile) return error ? <p className="error-text" role="alert">{error}</p> : <p>Lade…</p>;
  return (
    <form className="stack card" onSubmit={save}>
      <div className="field">
        <label htmlFor={`au-name-${userId}`}>Benutzername</label>
        <input id={`au-name-${userId}`} value={username} minLength={3} maxLength={32} pattern="[A-Za-z0-9_\-]+" title="Nur A-Z, a-z, 0-9, - und _" onChange={(e) => setUsername(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={`au-mail-${userId}`}>E-Mail-Adresse</label>
        <input id={`au-mail-${userId}`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <ul className="list-reset muted">
        <li>Status: {profile.status} · E-Mail {profile.emailVerifiedAt ? 'bestätigt' : 'nicht bestätigt'}</li>
        <li>Registriert: {new Date(profile.createdAt).toLocaleString('de-DE')}</li>
        <li>Gruppen: {profile.groupCount} · aktive Sitzungen: {profile.activeSessions}</li>
      </ul>
      <div className="row">
        <button className="primary" type="submit">Speichern</button>
        <button type="button" onClick={sendReset}>Passwort-Reset-Link senden</button>
      </div>
      {message && <p role="status">{message}</p>}
      {error && <p className="error-text" role="alert">{error}</p>}
    </form>
  );
}

function SmtpSettingsCard() {
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [secure, setSecure] = useState(false);
  const [allowSelfSigned, setAllowSelfSigned] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [testTo, setTestTo] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.smtp().then(({ smtp }) => {
      if (smtp.configured) {
        setEnabled(!!smtp.enabled);
        setHost(smtp.host ?? '');
        setPort(String(smtp.port ?? 587));
        setSecure(!!smtp.secure);
        setAllowSelfSigned(!!smtp.allowSelfSigned);
        setUsername(smtp.username ?? '');
        setPasswordSet(!!smtp.passwordSet);
        setFromAddress(smtp.fromAddress ?? '');
        setFromName(smtp.fromName ?? '');
      }
      setLoaded(true);
    }).catch((e) => setError((e as Error).message));
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const { smtp } = await adminApi.saveSmtp({
        enabled,
        host,
        port: Number(port),
        secure,
        allowSelfSigned,
        username,
        fromAddress,
        fromName,
        ...(clearPassword ? { password: '' } : password ? { password } : {}),
      });
      setPasswordSet(!!smtp.passwordSet);
      setPassword('');
      setClearPassword(false);
      setMessage('SMTP-Einstellungen gespeichert.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const r = await adminApi.testSmtp(testTo || undefined);
      if (r.ok) setMessage(testTo ? `Verbindung ok, Testmail an ${testTo} gesendet.` : 'Verbindung zum SMTP-Server ok.');
      else setError(`Test fehlgeschlagen: ${r.error}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!loaded && !error) return null;

  return (
    <form className="card stack" onSubmit={save}>
      <h2>E-Mail-Versand (SMTP)</h2>
      <p className="muted">
        Ohne aktivierten SMTP-Versand werden E-Mails (Verifizierung, Passwort-Reset, Einladungen) nur protokolliert. Gespeicherte Einstellungen
        gelten sofort; der Test nutzt die gespeicherten Werte.
      </p>
      <label className="row">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> SMTP-Versand aktiv
      </label>
      <label>Server <input value={host} onChange={(e) => setHost(e.target.value)} required /></label>
      <div className="row">
        <label>Port <input type="number" min={1} max={65535} value={port} onChange={(e) => setPort(e.target.value)} required /></label>
        <label className="row"><input type="checkbox" checked={secure} onChange={(e) => setSecure(e.target.checked)} /> TLS von Beginn an (meist Port 465; sonst STARTTLS)</label>
      </div>
      <label className="row"><input type="checkbox" checked={allowSelfSigned} onChange={(e) => setAllowSelfSigned(e.target.checked)} /> Selbstsignierte Zertifikate akzeptieren (Zertifikatsprüfung aus)</label>
      <label>Benutzername<input value={username} autoComplete="off" onChange={(e) => setUsername(e.target.value)} /></label>
      <label>
        Passwort {passwordSet && !clearPassword && <span className="muted">(gesetzt, leer lassen zum Beibehalten)</span>}
        <input type="password" value={password} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />
      </label>
      {passwordSet && (
        <label className="row"><input type="checkbox" checked={clearPassword} onChange={(e) => setClearPassword(e.target.checked)} /> Gespeichertes Passwort entfernen</label>
      )}
      <label>Absenderadresse <input type="email" value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} required /></label>
      <label>Absendername <input value={fromName} onChange={(e) => setFromName(e.target.value)} /></label>
      <div className="row">
        <button className="primary" type="submit" disabled={busy}>Speichern</button>
      </div>
      <fieldset className="stack">
        <legend>Test (gespeicherte Einstellungen)</legend>
        <label>Testmail an (optional) <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} /></label>
        <button type="button" onClick={test} disabled={busy || !host}>Verbindung testen</button>
      </fieldset>
      {message && <p role="status">{message}</p>}
      {error && <p className="error-text" role="alert">{error}</p>}
    </form>
  );
}

function BranchArchiveCard() {
  const [query, setQuery] = useState('');
  const [branches, setBranches] = useState<StoreBranch[]>([]);

  const load = useCallback(() => adminApi.storeBranches(query.trim() || undefined).then((r) => setBranches(r.branches)), [query]);

  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  async function toggle(b: StoreBranch) {
    await adminApi.setBranchArchived(b.id, !b.archivedAt);
    await load();
  }

  return (
    <div className="card stack">
      <h2>Geschäfte archivieren</h2>
      <p className="muted">Archivierte Filialen können für neue Listen nicht mehr ausgewählt werden. Bestehende Listen und Einkäufe bleiben unverändert.</p>
      <input type="search" aria-label="Geschäfte suchen" placeholder="Suche nach Kette, Filiale oder Ort" value={query} onChange={(e) => setQuery(e.target.value)} />
      {branches.length === 0 && <p className="muted">Keine Geschäfte gefunden.</p>}
      <ul className="list-reset stack">
        {branches.map((b) => (
          <li key={b.id} className="row between">
            <span>
              <strong>{b.chain.name}</strong> - {b.name}
              {b.city ? ` · ${b.city}` : ''} {b.archivedAt && <span className="badge">archiviert</span>}
            </span>
            <button onClick={() => toggle(b)}>{b.archivedAt ? 'Wiederherstellen' : 'Archivieren'}</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AdminPage() {
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [anomalies, setAnomalies] = useState<Array<Record<string, unknown>>>([]);

  useEffect(() => {
    adminApi.systemStatus().then(setStatus);
    adminApi.anomalies('PENDING').then((r) => setAnomalies(r.anomalies));
  }, []);

  const loadUsers = useCallback(() => adminApi.users(userQuery.trim() || undefined).then((r) => setUsers(r.users)), [userQuery]);

  useEffect(() => {
    const t = setTimeout(() => void loadUsers(), 250);
    return () => clearTimeout(t);
  }, [loadUsers]);

  async function suspend(id: string, status: string) {
    await adminApi.setUserStatus(id, status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE');
    await loadUsers();
  }

  async function decide(id: string, decision: 'CONFIRMED' | 'REMOVED') {
    await adminApi.decideAnomaly(id, decision);
    adminApi.anomalies('PENDING').then((r) => setAnomalies(r.anomalies));
  }

  return (
    <div className="stack">
      <h1>Administration</h1>

      <div className="card stack">
        <h2>Systemstatus (ADM-006)</h2>
        {status && (
          <ul className="list-reset">
            <li>API: {String(status.api)}</li>
            <li>Datenbank: {String(status.database)}</li>
            <li>Queue: {JSON.stringify(status.queue)}</li>
            <li>Sync (24h): {JSON.stringify(status.sync)}</li>
          </ul>
        )}
      </div>

      <div className="card stack">
        <h2>Auffällige Preise (ANOM-001..007)</h2>
        {anomalies.length === 0 && <p className="muted">Keine offenen Auffälligkeiten.</p>}
        {anomalies.map((a) => (
          <div key={a.id as string} className="row between">
            <span>
              {a.type as string} · Abweichung {a.deviationPct as string}%
            </span>
            <div className="row">
              <button onClick={() => decide(a.id as string, 'CONFIRMED')}>Als korrekt bestätigen</button>
              <button className="danger" onClick={() => decide(a.id as string, 'REMOVED')}>
                Aus Auswertung entfernen
              </button>
            </div>
          </div>
        ))}
      </div>

      <SmtpSettingsCard />

      <PurchaseHistoryImport />

      <BranchArchiveCard />

      <div className="card stack">
        <h2>Benutzer (ADM-001, ohne Einsicht in private Listen)</h2>
        <input type="search" aria-label="Benutzer suchen" placeholder="Suche nach Benutzername oder E-Mail" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} />
        {users.length === 0 && <p className="muted">Keine Benutzer gefunden.</p>}
        <ul className="list-reset stack">
          {users.map((u) => (
            <li key={u.id} className="stack">
              <div className="row between">
                <span>
                  <strong>{u.username ?? '(kein Benutzername)'}</strong> · {u.email} <span className="badge">{u.status}</span>
                </span>
                <div className="row">
                  <button onClick={() => setOpenUserId(openUserId === u.id ? null : u.id)}>{openUserId === u.id ? 'Schließen' : 'Profil'}</button>
                  <button onClick={() => suspend(u.id, u.status)}>{u.status === 'ACTIVE' ? 'Sperren' : 'Entsperren'}</button>
                </div>
              </div>
              {openUserId === u.id && <UserProfileEditor userId={u.id} onChanged={() => void loadUsers()} />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
